import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
	ApButtonIcon,
	ApLoaderDefault,
	ApLoaderText,
	ApMenu,
	ApMenuItem,
	ApSimpleDropdown,
	colors,
} from '@alixpartners/ui-components';
import { EXPORT_OPTIONS } from '../../Legal/utils';
import {
	FilterWrapper,
	Header,
	Label,
	LoadingContainer,
	NoData,
	SectionWrapper,
	Title,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './Charts.styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import useFetchData from '../../../../../Hooks/useFetchData';
import {
	getCostPerLawyerByFirmName,
	getLegalFirmnames,
	getTopFirmName,
} from '../../../../../utils/apiRequests';
import { renderToString } from 'react-dom/server';
import { formatSpendWithDecimalAndSymbol } from '../../../../../utils/number';
import { SettingsContext } from '../../../../../contexts/SettingsContext';
import { FONT_STYLE } from '../../../../utils';
import { isNull } from 'lodash';

const CostPerLawyerByFirmName = ({ chartTitle }) => {
	const chartRef = useRef();
	const { hideDecimals } = useContext(SettingsContext);
	const [firmName, setFirmName] = useState({});
	const [isOptionsLoading, setIsOptionsLoading] = useState(true);
	const [firmOptions, setFirmOptions] = useState([]);
	const [isChartLoading, setIsChartLoading] = useState(true);
	const [chartOptions, setChartOptions] = useState({
		chart: {
			type: 'bar',
		},
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		title: { text: '' },
		xAxis: {
			categories: [],
			title: {
				text: 'Years',
				style: { ...FONT_STYLE },
			},
			lineColor: colors.black,
			gridLineWidth: 0,
			labels: {
				style: { ...FONT_STYLE },
			},
		},
		yAxis: {
			title: {
				text: 'Cost Per Lawyer ($)',
				style: { ...FONT_STYLE },
			},
			gridLineWidth: 0,
			lineColor: colors.black,
			lineWidth: 1,
		},
		tooltip: {
			shared: true,
			useHTML: true,
			borderWidth: 0,
			backgroundColor: colors.white,
			outside: true,
			borderRadius: 2,
			formatter: function() {
				return renderToString(
					<TooltipContainer>
						<TooltipItemTitle>{this.x}</TooltipItemTitle>
						<TooltipItem key={`${this.x}-${this.y}`}>
							<span>
								<TooltipItemName>Cost</TooltipItemName>
							</span>
							<TooltipItemValue>
								{formatSpendWithDecimalAndSymbol(this.y, '$', hideDecimals)}
							</TooltipItemValue>
						</TooltipItem>
					</TooltipContainer>
				);
			},
		},
	});

	const { data: topFirmName, isLoading: isFirmNameLoading } = useFetchData(
		getTopFirmName
	);

	useEffect(
		() => {
			setIsChartLoading(true);
			if (Object.keys(firmName).length > 0) {
				const payload = {
					firm_name: firmName.value,
				};
				try {
					getCostPerLawyerByFirmName(payload)
						.then(res => {
							if (res.succeeded) {
								setChartOptions(prevState => ({
									...prevState,
									xAxis: {
										...prevState.xAxis,
										categories:
											res.data.map(item => item.year.toString()) || [],
									},
									series: [
										{
											name: 'Cost Per Lawyer',
											data: res.data.map(item => item.cost_per_lawyer) || [],
											color: '#032E45',
										},
									],
								}));
							}
						})
						.finally(() => setIsChartLoading(false));
				} catch (error) {
					console.log(error);
				}
			}
		},
		[firmName]
	);

	useEffect(() => {
		try {
			getLegalFirmnames()
				.then(res => {
					if (res.succeeded) {
						setFirmOptions(
							res.data.map(x => {
								return { label: x.firm_name, value: x.firm_name };
							})
						);
						setFirmName({
							label: res.data[0].firm_name,
							value: res.data[0].firm_name,
						});
					}
				})
				.finally(() => setIsOptionsLoading(false));
		} catch (error) {
			console.log(error);
		}
	}, []);

	useEffect(
		() => {
			if (!isNull(topFirmName)) {
				if (Object.keys(topFirmName?.data).length > 0) {
					setFirmName({
						label: topFirmName?.data?.firm_name,
						value: topFirmName?.data?.firm_name,
					});
				}
			}
		},
		[topFirmName]
	);

	const handleChartExport = option => {
		const chartInstance = chartRef?.current?.chart;
		if (chartInstance && option.value === 'png') {
			chartInstance.exportChart(
				{
					filename: chartTitle,
				},
				{
					chart: { width: 1200 },
					title: {
						text: chartTitle,
						align: 'left',
						style: {
							fontSize: '16px',
							fontWeight: 'bold',
							fontFamily: 'Roboto',
						},
					},
					subtitle: {
						text: `Firm Name: ${firmName?.value || ''}`,
						align: 'left',
						style: {
							fontSize: '14px',
							fontFamily: 'Roboto',
						},
					},
				}
			);
		}
		if (option.value === 'xlsx') {
			this.handleChartDataExport();
		}
	};

	return (
		<SectionWrapper>
			<Header>
				<Title>{chartTitle}</Title>
				<ApMenu button={<ApButtonIcon iconName="download" />}>
					{EXPORT_OPTIONS.map(option => (
						<ApMenuItem
							key={option.value}
							onClick={() => handleChartExport(option)}
						>
							{option.label}
						</ApMenuItem>
					))}
				</ApMenu>
			</Header>
			{isChartLoading ? (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			) : (
				<>
					{chartOptions.xAxis.categories.length === 0 ? (
						<NoData>No data available</NoData>
					) : (
						<>
							<FilterWrapper>
								{(!isOptionsLoading || !isFirmNameLoading) && (
									<>
										<Label>Select Firm Name: </Label>
										<ApSimpleDropdown
											options={firmOptions}
											selected={firmName}
											onChange={e => setFirmName(e)}
										/>
									</>
								)}
							</FilterWrapper>

							<HighchartsReact
								highcharts={Highcharts}
								options={chartOptions}
								ref={chartRef}
							/>
						</>
					)}
				</>
			)}
		</SectionWrapper>
	);
};

CostPerLawyerByFirmName.propTypes = {
	chartTitle: PropTypes.string.isRequired,
};

export default CostPerLawyerByFirmName;
